// A FAILED WRITE IS SAID OUT LOUD. `.catch(() => {})` on a post/put/patch/delete meant the server
// refused and the screen carried on as if it had worked — a deleted day-book entry still there, a
// budget never saved, a checklist note shown that the database never accepted. Measured 2 Oct 2026:
// 27 such writes, 2 deliberate. See docs/findings/silent-refusals-2026-10-02.md.
//
// Usage:  api.delete(url).then(load).catch(reportError('delete this entry', load))
//   what  — the act, as "Could not <what>" reads it
//   after — optional; runs after the message, e.g. reload so the screen shows what was ACTUALLY saved
//
// scripts/check-write-errors.js (run as `prebuild`, so on every deploy) fails the build on a write
// whose failure reaches nobody — an empty catch OR no catch at all. A helper alone is one more thing a
// future author has to know about; the gate is what stops the next one.
export const reportError = (what, after) => (e) => {
  const why = e?.response?.data?.error || e?.message || 'no reason was given';
  window.alert(`Could not ${what}.\n\n${why}\n\nThe server did not accept it.`);
  if (typeof after === 'function') after();
};

// A REASON THAT IS REQUIRED IS ASKED FOR UNTIL GIVEN, OR CANCELLED — never silently dropped.
// window.prompt returns null on Cancel and '' on an empty OK. The hand-written gates treated both
// the same (`if (!reason) return;`), so pressing OK on an empty box did nothing and said nothing;
// two of them called .trim() on the null and threw on Cancel. Returns the trimmed reason, or null
// when the user cancelled — callers `if (reason == null) return;` and nothing more.
export const askReason = (question, { defaultValue = '' } = {}) => {
  let q = question;
  for (;;) {
    const r = window.prompt(q, defaultValue);
    if (r == null) return null;                    // Cancel — the user's own choice, nothing to say
    if (r.trim()) return r.trim();
    q = 'A reason is required — nothing has been changed yet.\n\n' + question;
  }
};

// WHY A READ FAILED, AS A REASON A SCREEN CAN SHOW IN PLACE OF ITS DATA. A failed read must never render
// as an empty list or a zero: "No purchase invoices yet" over a 500 states a fact that is false. Measured
// 3 Oct 2026: 379 reads did exactly that. See docs/findings/swallowed-reads-2026-10-03.md.
export const whyNotLoaded = (e) =>
  e?.response?.data?.error || (e?.response ? `the server answered ${e.response.status}` : 'no answer from the server');
